import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withKeys as _withKeys, withModifiers as _withModifiers, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "radio-group timeline-paths" }
const _hoisted_2 = { class: "title" }
const _hoisted_3 = { class: "chips" }
const _hoisted_4 = { class: "radio-group" }
const _hoisted_5 = { class: "title" }
const _hoisted_6 = { class: "radio-group" }
const _hoisted_7 = { class: "radio-group" }
const _hoisted_8 = { class: "radio-group" }

import { ref, computed, watch, onMounted, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';

import { config, setConfig } from '@services/user-config';
import { fragment } from '@services/route-fragment';
import { t } from '@services/l10n';
import { showError } from '@services/utils/dialog';
import * as utils from '@services/utils/common';
import * as nativex from '@native';

import NcButton from '@nextcloud/vue/components/NcButton';
import NcTextField from '@nextcloud/vue/components/NcTextField';
import type { IConfig } from '@typings';


export default /*@__PURE__*/_defineComponent({
  __name: 'Settings',
  props: {
    open: { type: Boolean }
  },
  emits: ["update:open"],
  setup(__props: any, { emit: __emit }) {

const NcAppSettingsDialog = defineAsyncComponent(() => import('@nextcloud/vue/components/NcAppSettingsDialog'));
const NcAppSettingsSection = defineAsyncComponent(() => import('@nextcloud/vue/components/NcAppSettingsSection'));
const NcCheckboxRadioSwitch = defineAsyncComponent(() => import('@nextcloud/vue/components/NcCheckboxRadioSwitch'));
const NcChip = defineAsyncComponent(() => import('@nextcloud/vue/components/NcChip'));

const props = __props;

const emit = __emit;

const router = useRouter();

const localFolders = ref<nativex.LocalFolderConfig[]>([]);
const names = {
  header: t('memories', 'Settings'),
  general: t('memories', 'General'),
  viewer: t('memories', 'Photo Viewer'),
  onthisday: t('memories', 'On This Day'),
  account: t('memories', 'Account'),
  folders: t('memories', 'Folders'),
  albums: t('memories', 'Albums'),
  map: t('memories', 'Map Tiles'),
};

const timelinePaths = computed((): string[] => {
  return (config.timeline_path || '').split(';').filter((p) => p && !p.startsWith('_'));
});

const isNative = computed((): boolean => {
  return nativex.has();
});

const user = computed((): string => {
  return utils.uid ?? String();
});

const highResCond = computed((): IConfig['high_res_cond_default'] => {
  return config.high_res_cond || config.high_res_cond_default || 'zoom';
});

const tileServers = computed(() => {
  return [...(config.map_tile_servers || [])];
});

watch(
  () => props.open,
  (value: boolean) => {
    fragment.if(value, fragment.types.settings);
  },
);

onMounted(() => {
  if (isNative.value) {
    refreshNativeConfig();
  }
});

utils.useBus('memories:fragment:pop:settings', onClose);

function onClose() {
  emit('update:open', false);
}

// Paths settings
async function addTimelinePath() {
  let folder: string;
  try {
    folder = await utils.chooseNcFolder(t('memories', 'Add a root to your timeline'));
  } catch {
    return;
  }

  if (!folder || timelinePaths.value.includes(folder)) return;
  await saveTimelinePaths([...timelinePaths.value, folder]);
}

async function removeTimelinePath(path: string) {
  const paths = timelinePaths.value.filter((p) => p !== path);
  if (!paths.length) {
    showError(t('memories', 'At least one timeline path is required'));
    return;
  }
  await saveTimelinePaths(paths);
}

async function saveTimelinePaths(paths: string[]) {
  const newPath = paths.join(';');
  if (newPath !== config.timeline_path) {
    await setConfig('timeline_path', newPath);
  }
}

async function chooseFoldersPath() {
  let newPath: string;
  try {
    const prompt = t('memories', 'Choose the root for the folders view');
    newPath = await utils.chooseNcFolder(prompt, config.folders_path);
  } catch (e) {
    console.warn('chooseNcFolder cancelled', e);
    return;
  }

  if (newPath !== config.folders_path) {
    await setConfig('folders_path', newPath);
  }
}

// General settings
async function updateSquareThumbs(val: boolean) {
  await setConfig('square_thumbs', val);
}

async function updateEnableTopMemories(val: boolean) {
  await setConfig('enable_top_memories', val);
}

async function updateStackRawFiles(val: boolean) {
  await setConfig('stack_raw_files', val);
}

async function updateDedupIdentical(val: boolean) {
  await setConfig('dedup_identical', val);
}

async function updateShowOwnerNameTimeline(val: boolean) {
  await setConfig('show_owner_name_timeline', val);
}

async function updateShowPhotoBasename(val: boolean) {
  await setConfig('show_photo_basename', val);
}

// Viewer settings
async function updateHighResCond(val: IConfig['high_res_cond']) {
  await setConfig('high_res_cond', val);
}

async function updateLivephotoAutoplay(val: boolean) {
  await setConfig('livephoto_autoplay', val);
}

async function updateLivephotoLoop(val: boolean) {
  await setConfig('livephoto_loop', val);
}

async function updateVideoLoop(val: boolean) {
  await setConfig('video_loop', val);
}

async function updateVideoAutoplay(val: boolean) {
  await setConfig('video_autoplay', val ? 'true' : 'false');
}

async function updateSidebarFilepath(val: boolean) {
  await setConfig('sidebar_filepath', val);
}

async function updateMetadataInSlideshow(val: boolean) {
  await setConfig('metadata_in_slideshow', val);
}

async function updateSlideshowDuration(val: string | number) {
  const n = typeof val === 'number' ? val : parseFloat(val);
  if (!Number.isFinite(n)) return;
  await setConfig('slideshow_duration', Math.min(60, Math.max(1, Math.round(n))));
}

// On This Day settings
async function updateOnThisDayRange(val: string | number) {
  const n = typeof val === 'number' ? val : parseFloat(val);
  if (!Number.isFinite(n)) return;
  await setConfig('onthisday_day_range', Math.min(7, Math.max(0, Math.round(n))));
}

async function updateOnThisDayPhotos(val: string | number) {
  const n = typeof val === 'number' ? val : parseFloat(val);
  if (!Number.isFinite(n)) return;
  await setConfig('onthisday_photos_per_year', Math.min(50, Math.max(1, Math.round(n))));
}

// Folders settings
async function updateShowHiddenFolders(val: boolean) {
  await setConfig('show_hidden_folders', val);
}

async function updateShowHiddenAlbums(val: boolean) {
  await setConfig('show_hidden_albums', val);
}

async function updateSortFolderMonth(val: boolean) {
  await setConfig('sort_folder_month', val);
}

// Albums settings
async function updateSortAlbumMonth(val: boolean) {
  await setConfig('sort_album_month', val);
}

// Map settings
async function updateMapTileServer(val: string) {
  await setConfig('map_tile_server_url', val);
}

// --------------- Native APIs start -----------------------------
function refreshNativeConfig() {
  localFolders.value = nativex.getLocalFolders();
}

function updateDeviceFolders() {
  nativex.setLocalFolders(localFolders.value);
}

function runNxSetup() {
  router.replace('/nxsetup');
}

async function logout() {
  if (
    await utils.confirmDestructive({
      title: t('memories', 'Sign out'),
      message: t('memories', 'Are you sure you want to log out {user}?', { user: user.value }),
      confirm: t('memories', 'Sign out'),
      confirmClasses: 'error',
      cancel: t('memories', 'Cancel'),
    })
  ) {
    nativex.logout();
  }
}

return (_ctx: any,_cache: any) => {
  return (props.open)
    ? (_openBlock(), _createBlock(_unref(NcAppSettingsDialog), {
        key: 0,
        id: "memories-settings",
        class: "memories-modal",
        open: props.open,
        "show-navigation": true,
        name: names.header,
        "onUpdate:open": onClose
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(NcAppSettingsSection), {
            id: "general-settings",
            name: names.general
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(t)('memories', 'Timeline Path')), 1),
                _createElementVNode("div", _hoisted_3, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timelinePaths.value, (path) => {
                    return (_openBlock(), _createBlock(_unref(NcChip), {
                      key: path,
                      text: path,
                      "aria-label-close": _unref(t)('memories', 'Remove {path} from timeline', { path }),
                      onClose: ($event: any) => (removeTimelinePath(path))
                    }, null, 8, ["text", "aria-label-close", "onClose"]))
                  }), 128)),
                  _createVNode(_unref(NcChip), {
                    class: "add-chip",
                    text: _unref(t)('memories', 'Add path'),
                    "aria-label": _unref(t)('memories', 'Add a folder to the timeline'),
                    variant: "tertiary",
                    "no-close": "",
                    role: "button",
                    tabindex: "0",
                    onClick: addTimelinePath,
                    onKeydown: [
                      _withKeys(addTimelinePath, ["enter"]),
                      _withKeys(_withModifiers(addTimelinePath, ["prevent"]), ["space"])
                    ]
                  }, null, 8, ["text", "aria-label", "onKeydown"])
                ])
              ]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).square_thumbs,
                "onUpdate:modelValue": updateSquareThumbs,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Square grid mode')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).enable_top_memories,
                "onUpdate:modelValue": updateEnableTopMemories,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Show past photos on top of timeline')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).stack_raw_files,
                "onUpdate:modelValue": updateStackRawFiles,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Stack RAW files with same name')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).dedup_identical,
                "onUpdate:modelValue": updateDedupIdentical,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'De-duplicate identical files')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).show_owner_name_timeline,
                "onUpdate:modelValue": updateShowOwnerNameTimeline,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Show photo owner name on timeline')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).show_photo_basename,
                "onUpdate:modelValue": updateShowPhotoBasename,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Show photo filename on timeline')), 1)
                ]),
                _: 1
              }, 8, ["model-value"])
            ]),
            _: 1
          }, 8, ["name"]),
          _createVNode(_unref(NcAppSettingsSection), {
            id: "viewer-settings",
            name: names.viewer
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).livephoto_autoplay,
                "onUpdate:modelValue": updateLivephotoAutoplay,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Autoplay Live Photos')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).livephoto_loop,
                "onUpdate:modelValue": updateLivephotoLoop,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Loop Live Photos')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).video_autoplay === 'true',
                disabled: _unref(config).video_autoplay === 'disallow',
                "onUpdate:modelValue": updateVideoAutoplay,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Autoplay Videos')), 1)
                ]),
                _: 1
              }, 8, ["model-value", "disabled"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).video_loop,
                "onUpdate:modelValue": updateVideoLoop,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Loop Videos')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).sidebar_filepath,
                "onUpdate:modelValue": updateSidebarFilepath,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Show full file path in sidebar')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).metadata_in_slideshow,
                "onUpdate:modelValue": updateMetadataInSlideshow,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Show metadata in slideshow')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcTextField), {
                label: _unref(t)('memories', 'Slideshow Duration (1-60 seconds)'),
                "label-visible": true,
                "model-value": _unref(config).slideshow_duration,
                type: "number",
                min: "1",
                max: "60",
                step: "1",
                "onUpdate:modelValue": updateSlideshowDuration
              }, null, 8, ["label", "model-value"]),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(t)('memories', 'High resolution image loading behavior')), 1),
                _createVNode(_unref(NcCheckboxRadioSwitch), {
                  "model-value": highResCond.value,
                  value: "zoom",
                  name: "vhrc_radio",
                  type: "radio",
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (updateHighResCond($event)))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('memories', 'Load high resolution image on zoom')), 1)
                  ]),
                  _: 1
                }, 8, ["model-value"]),
                _createVNode(_unref(NcCheckboxRadioSwitch), {
                  "model-value": highResCond.value,
                  value: "always",
                  name: "vhrc_radio",
                  type: "radio",
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (updateHighResCond($event)))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('memories', 'Always load high resolution image (not recommended)')), 1)
                  ]),
                  _: 1
                }, 8, ["model-value"]),
                _createVNode(_unref(NcCheckboxRadioSwitch), {
                  "model-value": highResCond.value,
                  value: "never",
                  name: "vhrc_radio",
                  type: "radio",
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (updateHighResCond($event)))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('memories', 'Never load high resolution image')), 1)
                  ]),
                  _: 1
                }, 8, ["model-value"])
              ])
            ]),
            _: 1
          }, 8, ["name"]),
          (isNative.value)
            ? (_openBlock(), _createBlock(_unref(NcAppSettingsSection), {
                key: 0,
                id: "account-settings",
                name: names.account
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_6, [
                    _createTextVNode(_toDisplayString(_unref(t)('memories', 'Logged in as {user}', { user: user.value })) + " ", 1),
                    _createVNode(_unref(NcButton), {
                      class: "setting-button",
                      onClick: logout
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('memories', 'Sign out')), 1)
                      ]),
                      _: 1
                    })
                  ])
                ]),
                _: 1
              }, 8, ["name"]))
            : _createCommentVNode("", true),
          (isNative.value)
            ? (_openBlock(), _createBlock(_unref(NcAppSettingsSection), {
                key: 1,
                id: "device-settings",
                name: _unref(t)('memories', 'Device Folders')
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_7, [
                    _createTextVNode(_toDisplayString(_unref(t)('memories', 'Local folders to include in the timeline view')) + " ", 1),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localFolders.value, (folder) => {
                      return (_openBlock(), _createBlock(_unref(NcCheckboxRadioSwitch), {
                        key: folder.id,
                        modelValue: folder.enabled,
                        "onUpdate:modelValue": [($event: any) => ((folder.enabled) = $event), updateDeviceFolders],
                        type: "switch"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(folder.name), 1)
                        ]),
                        _: 2
                      }, 1032, ["modelValue", "onUpdate:modelValue"]))
                    }), 128)),
                    _createVNode(_unref(NcButton), {
                      class: "setting-button",
                      onClick: _cache[3] || (_cache[3] = ($event: any) => (runNxSetup())),
                      variant: "secondary"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('memories', 'Run initial device setup')), 1)
                      ]),
                      _: 1
                    })
                  ])
                ]),
                _: 1
              }, 8, ["name"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(NcAppSettingsSection), {
            id: "folders-settings",
            name: names.folders
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(NcTextField), {
                label: _unref(t)('memories', 'Folders Path'),
                "label-visible": true,
                "model-value": _unref(config).folders_path,
                onClick: chooseFoldersPath,
                readonly: ""
              }, null, 8, ["label", "model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).show_hidden_folders,
                "onUpdate:modelValue": updateShowHiddenFolders,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Show hidden folders')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).sort_folder_month,
                "onUpdate:modelValue": updateSortFolderMonth,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Sort folders oldest-first')), 1)
                ]),
                _: 1
              }, 8, ["model-value"])
            ]),
            _: 1
          }, 8, ["name"]),
          _createVNode(_unref(NcAppSettingsSection), {
            id: "albums-settings",
            name: names.albums
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).sort_album_month,
                "onUpdate:modelValue": updateSortAlbumMonth,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Sort albums oldest-first')), 1)
                ]),
                _: 1
              }, 8, ["model-value"]),
              _createVNode(_unref(NcCheckboxRadioSwitch), {
                "model-value": _unref(config).show_hidden_albums,
                "onUpdate:modelValue": updateShowHiddenAlbums,
                type: "switch"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Show hidden albums')), 1)
                ]),
                _: 1
              }, 8, ["model-value"])
            ]),
            _: 1
          }, 8, ["name"]),
          (tileServers.value.length > 0)
            ? (_openBlock(), _createBlock(_unref(NcAppSettingsSection), {
                key: 2,
                id: "map-settings",
                name: names.map
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_8, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tileServers.value, (tile) => {
                      return (_openBlock(), _createBlock(_unref(NcCheckboxRadioSwitch), {
                        key: tile.name,
                        "model-value": _unref(config).map_tile_server_url,
                        value: tile.url,
                        name: "map_style_radio",
                        type: "radio",
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (updateMapTileServer($event)))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(tile.name), 1)
                        ]),
                        _: 2
                      }, 1032, ["model-value", "value"]))
                    }), 128))
                  ])
                ]),
                _: 1
              }, 8, ["name"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(NcAppSettingsSection), {
            id: "onthisday-settings",
            name: names.onthisday
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(NcTextField), {
                label: _unref(t)('memories', 'Day range (0-7)'),
                "label-visible": true,
                "model-value": _unref(config).onthisday_day_range,
                type: "number",
                min: "0",
                max: "7",
                step: "1",
                "onUpdate:modelValue": updateOnThisDayRange,
                "helper-text": _unref(t)('memories', 'Number of days before and after each anniversary')
              }, null, 8, ["label", "model-value", "helper-text"]),
              _createVNode(_unref(NcTextField), {
                label: _unref(t)('memories', 'Photos per year (1-50)'),
                "label-visible": true,
                "model-value": _unref(config).onthisday_photos_per_year,
                type: "number",
                min: "1",
                max: "50",
                step: "1",
                "onUpdate:modelValue": updateOnThisDayPhotos,
                "helper-text": _unref(t)('memories', 'Maximum number of photos to include per year')
              }, null, 8, ["label", "model-value", "helper-text"])
            ]),
            _: 1
          }, 8, ["name"])
        ]),
        _: 1
      }, 8, ["open", "name"]))
    : _createCommentVNode("", true)
}
}

})